import React, { useRef, useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { styled, alpha } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import { useTranslation } from "react-i18next";
import useSearchShortcut from "../hooks/useSearchShortcut";
import { useNavigate } from "react-router-dom";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
  marginBottom: theme.spacing(1.5),
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "20ch",
      "&:focus": { width: "30ch" },
    },
  },
}));

type Props = {
  value?: string;
  onChange?: (v: string) => void;
  onSubmit?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  autoFocus?: boolean;
};

export const TopSearch: React.FC<Props> = ({
  value,
  onChange,
  onSubmit,
  inputRef,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const innerRef = useRef<HTMLInputElement | null>(null);
  const ref = inputRef ?? innerRef;

  // keyboard shortcut wants a ref to focus
  useSearchShortcut(ref, "f");

  const [internalText, setInternalText] = useState("");
  const text = value ?? internalText;
  const setText = onChange ?? ((v: string) => setInternalText(v));

  const submit = () => {
    if (onSubmit) {
      onSubmit();
    }
    const q = text.trim();
    if (!q) return;
    navigate(`/search?query=${encodeURIComponent(q)}`, {
      viewTransition: true,
    });
  };

  useEffect(() => {
    if (autoFocus) {
      // focus next tick
      setTimeout(() => {
        try {
          ref.current?.focus();
        } catch {}
      }, 0);
    }
  }, [autoFocus, ref]);

  return (
    <Search>
      <SearchIconWrapper>
        <SearchIcon />
      </SearchIconWrapper>
      <StyledInputBase
        placeholder={t("search.placeholder", { defaultValue: "Search…" })}
        inputRef={ref}
        inputProps={{
          "aria-label": t("search.aria_label", { defaultValue: "search" }),
        }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
      />
    </Search>
  );
};

export default TopSearch;
